<template>
  <div>
    <v-container id="classCreate">
      <v-card max-width="900px">
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text v-if="editModel">
        <v-form ref="classForm">
          <v-text-field
            v-model="editModel.name"
            :label="$t('classEdit.class-name')"
            required
            :rules="[rules.required]"
            compact
            :hint="$t('classEdit.name-hint')"
          />
          <v-btn width="100%" color="primary" @click="lessonPlannerOpen = true">
            {{ $t('classEdit.lesson-planner') }} ({{ editModel.content.length }})
          </v-btn>
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('classEdit.enrolment-options') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
          <v-select
            v-model="editModel.enrolmentType"
            :items="enrolmentTypes"
            compact
            item-text="title"
            item-value="uid"
            :label="$t('classEdit.self-enrolment')"
            required
          />
          <v-combobox
            v-if="editModel.id === undefined && classes"
            v-model="editModel.copyFrom"
            :label="$t('classEdit.copy-members-from')"
            :items="classes"
            item-text="name"
            item-value="id"
            clearable single
            persistent-hint
            :hint="$t('classEdit.copy-hint')"
          />
          <br>
          <!-- PERMITTED DOMAIN FIELD -->
          <v-combobox
            v-if="editModel.enrolmentType === 'public'"
            v-model="temp.permittedDomain"
            :hide-no-data="!search"
            :items="items"
            :search-input.sync="search"
            hide-selected
            :label="$t('classEdit.restrict-email-domain-to')"
            :hint="$t('classEdit.permittedDomain-hint')"
            multiple
            chips
            underline
          >
            <template v-slot:no-data>
              <v-list-item>
                <span class="subheading">{{ $t('classEdit.add-domain') }} </span>
                <v-chip
                  class="chip-class"
                  label
                  small
                >
                  {{ search }}
                </v-chip>
              </v-list-item>
            </template>
            <template v-slot:selection="{ attrs, item, parent, selected }">
              <v-chip
                v-if="item === Object(item)"
                v-bind="attrs"
                :input-value="selected"
                label
                small
              >
                <span class="pr-2 chip-class">
                  {{ item.text }}
                </span>
                <v-icon
                  small
                  @click="parent.selectItem(item)"
                >
                  $delete
                </v-icon>
              </v-chip>
            </template>
            <template v-slot:item="{ index, item }">
              <v-text-field
                v-if="editing === item"
                v-model="editing.text"
                autofocus
                flat
                background-color="transparent"
                hide-details
                solo
                @keyup.enter="edit(index, item)"
              />
              <v-chip
                v-else
                dark
                label
                small
              >
                {{ item.text }}
              </v-chip>
              <v-spacer />
              <v-list-item-action @click.stop>
                <v-btn
                  icon
                  @click.stop.prevent="edit(index, item)"
                >
                  <v-icon>{{ editing !== item ? 'mdi-pencil' : 'mdi-check' }}</v-icon>
                </v-btn>
              </v-list-item-action>
            </template>
          </v-combobox>
          <v-row v-if="editModel.enrolmentType !== 'inviteOnly'">
            <v-col>
              <v-textarea
                v-model="editModel.greetText"
                :label="$t('classEdit.greeting-text')"
                rows="3"
                :hint="$t('classEdit.greettext-hint')"
                persistent-hint
              />
            </v-col>
          </v-row>
          <v-row v-if="editModel.enrolmentType !== 'inviteOnly'">
            <v-col>
              <v-text-field
                v-model="editModel.maxSize"
                type="number"
                single-line
                :label="$t('classEdit.max-class-size')"
                :hint="$t('classEdit.maxsize-hint')"
                persistent-hint
              />
            </v-col>
            <v-col>
              <v-switch
                    v-model="editModel.selfEnrolEmailVerification"
                    :label="$t('classEdit.email-verification-required')"
                    inset
                  />
            </v-col>
          </v-row>
          <v-row v-if="editModel.enrolmentType !== 'inviteOnly'">
             <v-col>
               <v-menu
                  ref="menu"
                  v-model="enrolmentMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="enrolmentEndDateText"
                      :label="$t('classEdit.enrolment-ends-on')"
                      :hint="$t('classEdit.enrolment-end-hint')"
                      persistent-hint
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker v-model="enrolmentEndsDate" no-title scrollable>
                    <v-spacer />
                    <v-btn text color="primary" @click="enrolmentMenu = false">
                      {{ $t('global.cancel') }}
                    </v-btn>
                    <v-btn text color="primary" @click="saveEnrolmentEndsDate">
OK
</v-btn>
                  </v-date-picker>
                </v-menu>
             </v-col>
           </v-row>
          </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('classEdit.log-in-options') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
          <v-row>
            <v-col>
              <v-switch
                v-model="editModel.allowSelfRegistration"
                :label="$t('classEdit.self-register')"
                inset
              />
              <v-switch
                v-model="editModel.allowGuestJoin"
                :label="$t('classEdit.allow-guests')"
                inset
              />
            </v-col>
          </v-row>
          </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                    {{ $t('classEdit.advanced-options') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
              <v-row>
                <v-col>
                  <v-select
                    v-model="editModel.editors"
                    :label="$t('classEdit.additional-admins')"
                    :items="editorsOrdered"
                    item-text="displayName"
                    item-value="id"
                    clearable
                    multiple
                    persistent-hint
                    chips
                    :disabled="!isOwner"
                    :hint="editorHint"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    v-model="editModel.email"
                    :label="$t('classEdit.class-support-address-label')"
                    :rules="[rules.emailRules]"
                    :hint="$t('classEdit.support-email-hint')"
                    persistent-hint
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    v-model="editModel.notificationEmail"
                    :label="$t('classEdit.class-notification-address-label')"
                    :rules="[rules.emailRules]"
                    :hint="$t('classEdit.notification-email-hint')"
                    persistent-hint
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
        </v-form>
      </v-card-text>
      <v-card-text>
        <div v-if="!valid">
        <span class="validateError">{{ $t('classEdit.validate-info') }}</span>
      </div>
      </v-card-text>
      <v-card-actions>
          <v-spacer />
          <v-btn color="blue darken-1" @click="cancel">
            {{ $t('global.cancel') }}
          </v-btn>
          <v-btn :disabled="!valid" color="success" @click="validate">
            {{ $t('global.save') }}
          </v-btn>
      </v-card-actions>
      </v-card>
    </v-container>
    <v-dialog v-if="editModel" v-model="lessonPlannerOpen" max-width="800px" persistent>
      <lesson-planner
        :module-selection="editModel.content === '' ? [] : editModel.content"
        @save="onLessonPlannerSave"
        @cancel="onLessonPlannerCancel"
      />
    </v-dialog>

    <v-dialog v-model="warnContentChange" max-width="500" persistent>
      <v-card>
        <v-card-title>{{ $t('classEdit.content-changed') }}</v-card-title>
        <v-card-text>
          <span v-html="$t('classEdit.content-changed-text')" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
              @click="confirmContentChange(false)"
            >
              {{ $t('global.cancel') }}
            </v-btn>
            <v-btn
              color="green darken-1"
              @click="confirmContentChange(true)"
            >
              {{ $t('global.continue') }}
            </v-btn>
          </v-card-actions>
        </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import moment from 'moment'
  import LessonPlanner from './LessonPlanner.vue'

  export default {
    name: 'ClassCreate',

    components: {
      'lesson-planner': LessonPlanner,
    },

    props: {
      model: {
        type: Object,
        required: true,
        default: undefined,
      },
      editors: {
        type: Array,
        required: false,
        default: undefined,
      },
      classes: {
        type: Array,
        required: false,
        default: undefined,
      },
    },

    data () {
      return {
        lessonPlannerOpen: false,
        previousContent: null,
        warnContentChange: false,
        loading: true,
        enrolmentMenu: false,
        enrolmentEndsDate: '',
        enrolmentEndsTitle: this.$t('classEdit.enrolment-ends-on'),
        activator: null,
        attach: null,
        editing: null,
        colors: ['gray'],
        editingIndex: -1,
        editModel: null,
        temp: {
          permittedDomain: null,
        },
        items: [],
        nonce: 1,
        menu: false,
        x: 0,
        search: null,
        y: 0,
        name: '',
        pattern: /^@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        rules: {
          required: value => !!value || this.$t('classEdit.required'),
          contentRules: value => !!value || this.$t('classEdit.requires-content-to-be-selected'),
          emailDomain: value => {
            if (!value) {
              return true
            }
            const splitValue = value.split(',')
            const pattern = /^@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            for (const csv of splitValue)
            {
              if (!pattern.test(csv)) return this.$t('classEdit.invalid-domain')
            }
            return true
          },
          emailRules:
            value => {
              const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
              if (!value) {
                return true
              }
              return pattern.test(value) || this.$t('account.edit.invalid-e-mail')
            },

        },
        enrolmentTypes: [
          { title: this.$t('classEdit.enrolmentType.public'), uid: 'public' },
          { title: this.$t('classEdit.enrolmentType.private'), uid: 'private' },
          { title: this.$t('classEdit.enrolmentType-inviteOnly'), uid: 'inviteOnly' }],
      }
    },

    computed: {
      editorsOrdered: function () {
        return [...this.editors].sort((a, b) => a.displayName.localeCompare(b.displayName))
      }, 
      valid: function () {
        // if there is a class name and the lesson planner has no content, return false
        return this.editModel?.name && this.editModel?.content.length > 0
      },
      title: function () {
        return this.editModel?.id ? this.$t('classEdit.edit-class') : this.$t('classEdit.create-class')
      },
      isOwner: function () {
        return true // !this.editModel?.id || (this.editModel.creator && this.editModel.creator.id === this.$store.getters.currentUserProfile.user.id)
      },
      editorHint: function () {
        return this.isOwner ? this.$t('classEdit.additionalEditor-hint') : this.$t('classEdit.additionalEditor-hint-alt')
      },
      isExistingClass: function () {
        return this.editModel?.id !== undefined && this.editModel.id !== null
      },
      enrolmentEndDateText () {
        return this.editModel.enrolmentEndsAt ? moment(this.editModel.enrolmentEndsAt).format('DD-MM-YYYY') : ''
      },
    },

    watch: {
      enrolmentMenu: function (newVal) {
        if (newVal === true) {
          this.enrolmentEndsDate = this.editModel.enrolmentEndsAt ? (new Date(this.editModel.enrolmentEndsAt)).toISOString().substr(0, 10) : ''
        }
      },
      'temp.permittedDomain': function (newVal, prev) {
        if (prev && newVal) {
          if (newVal.length === prev.length) return
        }
        this.temp.permittedDomain = newVal && newVal.map(v => {
          if (typeof v === 'string') {
            if (!this.pattern.test(v)) {
              return this.$t('classEdit.invalid-domain')
            } else {
              v = {
                text: v,
              }
              this.items.push(v)
              this.nonce++
            }
          }
          return v
        })
      },
      model: function (newVal) {
        this.setup(newVal)
      },
    },

    mounted () {
      this.setup(this.model)
    },

    methods: {
      setup (newVal) {
        // This can happen at save, so ignore self-triggered updates
        if (newVal === this.editModel) return
        this.temp.permittedDomain =
          (newVal.permittedDomain && typeof newVal.permittedDomain === 'string') ? newVal.permittedDomain.split(',').map(x => ({ text: x }))
          : Array.isArray(newVal.permittedDomain) ? newVal.permittedDomain : []
        this.editModel = JSON.parse(JSON.stringify(newVal)) // deep copy the incoming model
        // console.log(JSON.stringify(this.editModel))
        this.previousContent = newVal.content
      },
      save () {
        if (this.editModel.copyFrom) {
          this.editModel.copyFrom = this.editModel.copyFrom.id
        }
        // move temp fields back to the edited model before deep copying back to the original
        this.editModel.permittedDomain = this.temp.permittedDomain ? this.temp.permittedDomain.filter(x => typeof x !== 'string').map(x => x.text) : []
        this.$emit('save', this.editModel)
      },
      confirmContentChange (confirm) {
        this.warnContentChange = false
        if (confirm) {
          this.save()
        }
      },
      async validate () {
        if ((this.editModel?.id !== undefined && this.editModel.id !== null) &&
          (this.editModel.content.length !== this.previousContent.length ||
            !this.editModel.content.every((value, index) => value === this.previousContent[index]))) {
          this.warnContentChange = true
          return
        }
        this.save()
      },
      onLessonPlannerSave (content) {
        this.lessonPlannerOpen = false
        this.lessonPlannerChanged = true
        this.editModel.content = content
      },
      onLessonPlannerCancel () {
        this.lessonPlannerOpen = false
      },
      cancel () {
        this.$emit('cancel')
      },
      saveEnrolmentEndsDate () {
        this.editModel.enrolmentEndsAt = moment(this.enrolmentEndsDate).toDate()
        this.enrolmentMenu = false
      },
      edit (index, item) {
        if (!this.editing) {
          this.editing = item
          this.editingIndex = index
        } else {
          this.editing = null
          this.editingIndex = -1
        }
      },
      filter (item, queryText, itemText) {
        if (item.header) return false

        const hasValue = val => val != null ? val : ''

        const text = hasValue(itemText)
        const query = hasValue(queryText)

        return text.toString()
          .toLowerCase()
          .indexOf(query.toString().toLowerCase()) > -1
      },
    },
  }
</script>

<style>
.warningtext {
  color: red !important;
  font-size: 11px;
  font-weight: bold;
  background: none;
}
.chip-class {
   text-transform: lowercase !important;
}
.validateError {
  color: red;
  font-size: 1em;
  background: none;
}
.invisible {
  opacity: 0;
}
</style>
